import { logGreyError } from 'owa-analytics';
import { isClientId, convertRestIdToEwsId } from 'owa-identifiers';

// Note: these do not distinguish between REST and EWS concatenated ids.
// The only difference is the encoding.  REST ids are EWS ids, except they replace + with - and / with _.
export type IdType =
    | 'ConcatenatedImmutableId'
    | 'ConcatenatedEntryId'
    | 'StoreObjectImmutableId'
    | 'ClientId'
    | 'UnknownId';

// EWS encodes its IDs as base64 encoded ConcatenatedIds . The second byte of a ConcatenatedId is the IdStorageType.
// So, you can decode the base64 and look at the second byte of an EWS id. If it's a 9, then the id is an immutable id.
// If it's something else, it's a different IdStorageType.
// REST ids are similar to EWS ids, except they replace + with - and / with _.
// CAPIv3 (and maybe other services) are raw store IDs encoded as base64, sometimes with a spurious trailing 0.
// The first byte of a raw store ID is the id length.  If that is 46, then it's either an immutable id or a folder id.  If it's immutable, the 20th byte in the ID will also be 13.
export function getIdType(id: string): IdType {
    let rv: IdType = 'UnknownId';

    try {
        if (!id) {
            rv = 'UnknownId';
        } else if (isClientId(id)) {
            rv = 'ClientId';
        } else {
            if (id.length % 4 === 1 && id.endsWith('0')) {
                // Some services return storeObjectIds with a spurious trailing 0. If the id is not a multiple of 4 and we see one of those 0, remove it.
                id = id.slice(0, -1);
            }

            // REST encoding uses - instead of + and _ instead of /, so replace those
            id = convertRestIdToEwsId(id);
            const arr = Uint8Array.from(atob(id), c => c.charCodeAt(0));

            if (arr[0] === 46 && arr[21] === 13) {
                // storeobjectid of immutable type
                rv = 'StoreObjectImmutableId';
            } else if ((arr[0] === 0 || arr[0] === 1) && arr[1] === 9) {
                // concatenatedid of immutable type
                rv = 'ConcatenatedImmutableId';
            } else if ((arr[0] === 0 || arr[0] === 1) && arr[1] === 3) {
                rv = 'ConcatenatedEntryId';
            }
        }
    } catch (e) {
        // unexpected since these are coming from the server, but assume a malformed id is not immutable
        /* eslint-disable owa-custom-rules/telemetry-event-name-format -- (https://aka.ms/OWALintWiki)
         * Baseline. DO NOT COPY AND PASTE! Grandfathering existing event names; Devs who touch this file should update Event Names to follow Outlook Telemetry Naming Standards
         *	> Invalid Outlook telemetry EventName "getIdType": must start with a capital letter (A–Z). */
        /* eslint-disable owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
         * BASELINE: DO NOT COPY/PASTE!
         *	> Argument at index 1 for logGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */

        // Errors resulting from enabling useUnknownInCatchVariables
        // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'Error | undefined'.
        // @ts-expect-error
        logGreyError('getIdType', undefined, e, { reason: getInvalidBase64Reason(id) });
        /* eslint-enable owa-custom-rules/telemetry-mailboxinfo-as-second-param */
        /* eslint-enable owa-custom-rules/telemetry-event-name-format  */
    }

    return rv;
}

export function getInvalidBase64Reason(id: string | undefined | null): string {
    let reason = 'unknown';

    try {
        if (!id) {
            reason = 'id is null or undefined';
        } else if (typeof id !== 'string') {
            reason = `id is not a string: ${typeof id}`;
        } else if (id.length % 4 !== 0) {
            reason = `id length is not a multiple of 4: ${id.length}. ${id.slice(
                0,
                4
            )}...${id.slice(-4)}`;
        } else {
            for (let i = 0; i < id.length; i++) {
                if (isNotBase64Char(id[i])) {
                    reason = `id contains invalid character at index ${i}: ${id[i]}`;
                    break;
                }
            }
        }
    } catch (e) {
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS18046: 'e' is of type 'unknown'.
        // @ts-expect-error
        reason = `unexpected error: ${e.message}`;
    }

    return reason;
}

function isNotBase64Char(char: string): boolean {
    return !/[A-Za-z0-9+/=]/.test(char);
}
